import Dexie from 'dexie';

/**
 * Multi-tenant, offline-first local datastore (Dexie v4).
 *
 * Schema v1 — legacy single-user prototype store.
 * Schema v2 — tenant-scoped offline-first SaaS store with an outbox
 * (`syncQueue`) for delta sync against PocketBase and versioning columns
 * for optimistic locking / 3-way merge conflict resolution.
 *
 * Invariants enforced here:
 *  - Every tenant-owned record carries `tenantId` (+ compound indexes) so
 *    queries can never accidentally cross a tenant boundary.
 *  - Every mutable record carries `mutationVersion` + `updatedAt` used by
 *    ConflictResolver (3-way merge) and SyncWorker optimistic locking.
 *  - Soft deletes via `deletedAt` so deletions can be replicated as deltas
 *    instead of being lost when the device is offline at delete time.
 *  - Legacy v1 data is preserved by assigning it to the "Local Workspace"
 *    pseudo-tenant — never dropped during upgrade.
 */

// Pseudo-tenant assigned to records created before authentication existed.
export const LOCAL_WORKSPACE_TENANT_ID = 'org_local_workspace';

// Sentinel user id for records written before PocketBase auth existed.
export const LOCAL_USER_ID = 'user_local';

/** Status values for the per-record sync lifecycle. */
export const SYNC_STATUS = {
  SYNCED: 'synced',
  PENDING: 'pending', // local mutation awaiting push
  CONFLICT: 'conflict', // push rejected; needs 3-way merge review
  ERROR: 'error' // exhausted retries but retained in outbox
};

/**
 * Schema v2.
 *
 * Index notes:
 *  - `forms.syncQueue`-style outbox uses `id` (uuid string) as PRIMARY KEY,
 *    not `++seq`: the uuid doubles as an idempotency key so replaying a
 *    batch after a crash cannot enqueue duplicate deltas.
 *  - `[tenantId+status]`, `[tenantId+updatedAt]`, `[tenantId+formId]`
 *    compound indexes back the FormLibrary facet filters and the sync pull
 *    cursor query (`[tenantId+updatedAt]` range scan).
 */
const SCHEMA_V2 = {
  // Tenant-scoped certificate records.
  forms: `
    ++id,
    formId,
    instanceId,
    serialNumber,
    status,
    createdAt,
    updatedAt,
    lastAccessedAt,
    tenantId,
    userId,
    syncStatus,
    mutationVersion,
    deletedAt,
    data,
    [formId+createdAt],
    [formId+status],
    [formId+updatedAt],
    [tenantId+status],
    [tenantId+updatedAt],
    [tenantId+formId],
    [tenantId+syncStatus]
  `,

  // Outbox: one delta per local mutation. Primary key is a client uuid so
  // enqueue is idempotent across crash-recovery replays.
  syncQueue: `
    id,
    tenantId,
    collection,
    recordId,
    action,
    attempts,
    nextAttemptAt,
    createdAt,
    status,
    [tenantId+createdAt],
    [status+nextAttemptAt]
  `,

  // CRM contacts cache (Track 5 will build UI over this).
  customers: `
    ++id,
    tenantId,
    name,
    type,
    email,
    phone,
    syncStatus,
    mutationVersion,
    deletedAt,
    updatedAt,
    [tenantId+name],
    [tenantId+type],
    [tenantId+updatedAt]
  `,

  // Site/appliance asset registry cache.
  properties: `
    ++id,
    tenantId,
    customerId,
    postcode,
    addressLine1,
    city,
    syncStatus,
    mutationVersion,
    deletedAt,
    updatedAt,
    [tenantId+postcode],
    [tenantId+customerId],
    [tenantId+updatedAt]
  `,

  templates: `
    ++id,
    name,
    formId,
    tenantId,
    isDefault,
    lastUsed,
    syncStatus,
    mutationVersion,
    deletedAt,
    data,
    [formId+lastUsed],
    [tenantId+formId]
  `,

  settings: `
    key,
    value,
    updatedAt
  `,

  // Per-tenant sync cursors & engine state (lastSyncTime etc).
  meta: `
    key
  `,

  backups: `
    ++id,
    tenantId,
    createdAt,
    size,
    formCount,
    data
  `
};

export class CertificateDB extends Dexie {
  constructor() {
    super('CertificateSystemDB');

    // ---- v1 (legacy, kept for upgrade path) -------------------------------
    this.version(1).stores({
      forms: `
        ++id,
        formId,
        instanceId,
        serialNumber,
        status,
        createdAt,
        updatedAt,
        lastAccessedAt,
        data,
        [formId+createdAt],
        [formId+status],
        [formId+updatedAt]
      `,
      templates: `
        ++id,
        name,
        formId,
        isDefault,
        lastUsed,
        data,
        [formId+lastUsed]
      `,
      settings: `
        key,
        value,
        updatedAt
      `,
      syncQueue: `
        ++seq,
        action,
        entityType,
        entityId,
        timestamp,
        [entityType+timestamp]
      `,
      backups: `
        ++id,
        createdAt,
        size,
        formCount,
        data
      `
    });

    // ---- v2 (multi-tenant offline-first) ----------------------------------
    // IndexedDB cannot change the primary key of an existing table in-place.
    // In v1, syncQueue had `++seq`. We drop the legacy syncQueue (syncQueue: null)
    // in v2 so that version(2) can cleanly create the outbox with `id` PK.
    this.version(2)
      .stores({
        ...SCHEMA_V2,
        syncQueue: null
      })
      .upgrade(async (tx) => {
        // 1. Re-scope every pre-existing record into the Local Workspace
        //    pseudo-tenant so nothing is orphaned or lost. Records keep
        //    pending sync status so they become first-class sync citizens
        //    once the user authenticates and links a real organization.
        await tx
          .table('forms')
          .toCollection()
          .modify((form) => {
            if (!form.tenantId) form.tenantId = LOCAL_WORKSPACE_TENANT_ID;
            if (!form.userId) form.userId = LOCAL_USER_ID;
            if (!form.syncStatus) form.syncStatus = SYNC_STATUS.PENDING;
            if (typeof form.mutationVersion !== 'number')
              form.mutationVersion = 1;
          });

        await tx
          .table('templates')
          .toCollection()
          .modify((tpl) => {
            if (!tpl.tenantId) tpl.tenantId = LOCAL_WORKSPACE_TENANT_ID;
            if (!tpl.syncStatus) tpl.syncStatus = SYNC_STATUS.PENDING;
            if (typeof tpl.mutationVersion !== 'number') tpl.mutationVersion = 1;
          });
      });

    // Recreate syncQueue with the uuid `id` primary key
    this.version(2.1).stores({
      syncQueue: SCHEMA_V2.syncQueue
    });

    // ---- v3 (Track 5: CRM link indexes on certificate records) ------------
    // Forms gain the customerId / propertyId relation columns (already
    // stamped by CustomerService when a draft is pre-filled from a property)
    // plus compound tenant indexes so certificate history per customer /
    // property can be queried without a full-table scan.
    this.version(3).stores({
      forms: `
        ++id,
        formId,
        instanceId,
        serialNumber,
        status,
        createdAt,
        updatedAt,
        lastAccessedAt,
        tenantId,
        userId,
        syncStatus,
        mutationVersion,
        deletedAt,
        data,
        customerId,
        propertyId,
        [formId+createdAt],
        [formId+status],
        [formId+updatedAt],
        [tenantId+status],
        [tenantId+updatedAt],
        [tenantId+formId],
        [tenantId+syncStatus],
        [tenantId+customerId],
        [tenantId+propertyId]
      `
    });

    // ---- v4 (Track 5: tenant-scoped [tenantId+createdAt] sort indexes) ----
    // CRM directory pages order by newest-first per tenant. The compound
    // [tenantId+createdAt] index is required for `.where(...).reverse()`
    // range scans in CustomerService; without it Dexie throws SchemaError
    // ("Key not indexed"). Index-only bump — no data migration needed.
    this.version(4).stores({
      forms: `
        ++id,
        formId,
        instanceId,
        serialNumber,
        status,
        createdAt,
        updatedAt,
        lastAccessedAt,
        tenantId,
        userId,
        syncStatus,
        mutationVersion,
        deletedAt,
        data,
        customerId,
        propertyId,
        [formId+createdAt],
        [formId+status],
        [formId+updatedAt],
        [tenantId+createdAt],
        [tenantId+status],
        [tenantId+updatedAt],
        [tenantId+formId],
        [tenantId+syncStatus],
        [tenantId+customerId],
        [tenantId+propertyId]
      `,
      customers: `
        ++id,
        tenantId,
        name,
        type,
        email,
        phone,
        syncStatus,
        mutationVersion,
        deletedAt,
        updatedAt,
        [tenantId+name],
        [tenantId+type],
        [tenantId+createdAt],
        [tenantId+updatedAt]
      `,
      properties: `
        ++id,
        tenantId,
        customerId,
        postcode,
        addressLine1,
        city,
        syncStatus,
        mutationVersion,
        deletedAt,
        updatedAt,
        [tenantId+postcode],
        [tenantId+customerId],
        [tenantId+createdAt],
        [tenantId+updatedAt]
      `
    });

    // ---- v5 (Dev 3: cross-device remote identity for CRM records) ---------
    // customers/properties get a `remoteId` column: the PocketBase record id
    // minted on-device at creation ([a-z0-9]{15}) and reused verbatim on
    // push/pull. Without it the local auto-increment key could never match
    // a server record — every pull duplicated rows and every UPDATE/DELETE
    // push targeted a nonexistent server id. (forms.instanceId already
    // serves this role for certificates; its format is normalized to PB's
    // in SyncWorker at push time.) Index-only + additive — no data rewrite.
    this.version(5).stores({
      customers: `
        ++id,
        remoteId,
        tenantId,
        name,
        type,
        email,
        phone,
        syncStatus,
        mutationVersion,
        deletedAt,
        updatedAt,
        [tenantId+name],
        [tenantId+type],
        [tenantId+createdAt],
        [tenantId+updatedAt],
        [tenantId+remoteId]
      `,
      properties: `
        ++id,
        remoteId,
        tenantId,
        customerId,
        postcode,
        addressLine1,
        city,
        syncStatus,
        mutationVersion,
        deletedAt,
        updatedAt,
        [tenantId+postcode],
        [tenantId+customerId],
        [tenantId+createdAt],
        [tenantId+updatedAt],
        [tenantId+remoteId]
      `
    });

    this.isInitialized = false;
    this.initPromise = null;

    // Live outbox listeners (registered lazily by OutboxManager).
    this._outboxListeners = new Set();
  }

  /** Ensure DB is open + defaults seeded before any service touches it. */
  async ensureReady() {
    if (this.isInitialized) return true;
    if (!this.initPromise) {
      this.initPromise = this.initialize();
    }
    return await this.initPromise;
  }

  async initialize() {
    try {
      await this.open();
      await this.initializeDefaults();
      this.isInitialized = true;
      return true;
    } catch (error) {
      console.error('Database initialization failed:', error);
      this.initPromise = null; // allow retry on next call
      throw error;
    }
  }

  async initializeDefaults() {
    const settingsCount = await this.settings.count();
    if (settingsCount === 0) {
      await this.settings.bulkPut([
        { key: 'appVersion', value: '2.0.0', updatedAt: new Date() },
        { key: 'theme', value: 'light', updatedAt: new Date() },
        { key: 'autoBackup', value: true, updatedAt: new Date() },
        { key: 'backupFrequency', value: 'daily', updatedAt: new Date() },
        { key: 'schemaVersion', value: 4, updatedAt: new Date() }
      ]);
    }
  }

  // ---------------------------------------------------------------------
  // Sync cursor helpers (per-tenant pull watermark)
  // ---------------------------------------------------------------------

  async getMeta(key, defaultValue = null) {
    const row = await this.meta.get(key);
    return row ? row.value : defaultValue;
  }

  async setMeta(key, value) {
    await this.meta.put({ key, value, updatedAt: new Date() });
  }

  /**
   * Register a callback fired whenever the outbox gains a new item or an
   * item settles. Used by useSync() to update the UI without polling.
   */
  onOutboxChange(cb) {
    this._outboxListeners.add(cb);
    return () => this._outboxListeners.delete(cb);
  }

  _notifyOutbox() {
    this._outboxListeners.forEach((cb) => {
      try {
        cb();
      } catch {
        /* listener errors must never break writes */
      }
    });
  }

  /** Count of outbox items still awaiting a successful push. */
  async pendingSyncCount(tenantId = null) {
    let col = this.syncQueue.where('status').notEqual('done');
    if (tenantId) col = col.and((item) => item.tenantId === tenantId);
    return await col.count();
  }
}

export const db = new CertificateDB();

// Database utilities
export const dbUtils = {
  async getSetting(key, defaultValue = null) {
    const setting = await db.settings.get(key);
    return setting ? setting.value : defaultValue;
  },

  async setSetting(key, value) {
    await db.settings.put({ key, value, updatedAt: new Date() });
  },

  async getFormsByType(formId, tenantId = null) {
    if (tenantId) {
      return await db.forms
        .where('[tenantId+formId]')
        .equals([tenantId, formId])
        .toArray();
    }
    return await db.forms.where('formId').equals(formId).toArray();
  },

  async getRecentForms(limit = 10, tenantId = null) {
    if (tenantId) {
      return await db.forms
        .where('[tenantId+updatedAt]')
        .between([tenantId, Dexie.minKey], [tenantId, Dexie.maxKey])
        .reverse()
        .limit(limit)
        .toArray();
    }
    return await db.forms.orderBy('updatedAt').reverse().limit(limit).toArray();
  },

  async updateFormAccess(id) {
    await db.forms.update(id, { lastAccessedAt: new Date() });
  }
};
